import { EventClickArg, EventContentArg, EventDropArg, EventMountArg } from "@fullcalendar/core";
import fiLocale from "@fullcalendar/core/locales/fi";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import momentPlugin from "@fullcalendar/moment";
import FullCalendar from "@fullcalendar/react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
    Box,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";
import * as moment from "moment";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import { CalendarDatePicker } from "./CalendarDatePicker";
import { CalendarItemDetailPopup } from "./CalendarItemDetailPopup";
import { ListStateIconPrivate } from "../List/ListStateIconPrivate";
import { DatabaseRecordType } from "ui/Scripts/source/models/enums";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";
import { AppToolButton } from "ui/Scripts/source/components/framework/Button/AppToolButton";

/* eslint-disable no-unused-vars */
export const enum CalendarViewType {
    Day = "dayGridDay",
    Week = "dayGridWeek",
    Month = "dayGridMonth"
}
/* eslint-enable no-unused-vars */

export interface ICalendarItem {
    id: string;
    companyId: string;
    recordId: string;
    databaseRecordType: DatabaseRecordType;
    title: string;
    start: string;
    end: string;
    color: string;
    allDay: boolean;
    editable: boolean;
    location?: string;
    formatedDate?: string;
    parentId?: string;
}

export type CalendarRefProps = {
    moveToDate: (date: moment.Moment) => void;
    moveToPrev: () => void;
    moveToNext: () => void;
};

interface CalendarProps {
    height?: string;
    events: ICalendarItem[];
    onGetEvents: (startDate: number, endDate: number) => void; //UTC dates
    onDateClick: (date: number) => void;
    onEventClick: (id: string) => void;
    onEventRemove: (id: string) => void;
    onSetEventStartDate: (id: string, startDate: number) => Promise<void>;
}

interface CalendarState {
    title: string;
    viewType: CalendarViewType;
    referenceDate?: Date;
    detailsVisible: boolean;
    detailsEvent?: ICalendarItem;
    detailsAnchor?: HTMLElement;
}

export const Calendar = forwardRef<CalendarRefProps, CalendarProps>(({ height = "calc(100vh - 380px)", ...props }: CalendarProps, ref) => {
    const calendarRef = useRef<FullCalendar | null>();

    const getEvents = (start: Date, end: Date) => {
        props.onGetEvents(start.getTime(), end.getTime());
    };

    const handleDateChange = (date: moment.Moment) => {
        const calendarApi = calendarRef?.current?.getApi();
        calendarApi.gotoDate(date.toISOString());
        setState((old) => ({
            ...old,
            title: calendarApi.view?.title,
            referenceDate: calendarApi.getDate(),
        }));
        getEvents(calendarApi.view.activeStart, calendarApi.view.activeEnd);
    };

    const handlePrev = () => {
        const calendarApi = calendarRef?.current?.getApi();
        calendarApi.prev();
        setState((old) => ({
            ...old,
            title: calendarApi.view?.title,
            referenceDate: calendarApi.getDate(),
        }));
        getEvents(calendarApi.view.activeStart, calendarApi.view.activeEnd);
    };

    const handleNext = () => {
        const calendarApi = calendarRef?.current?.getApi();
        calendarApi.next();
        setState((old) => ({
            ...old,
            title: calendarApi.view?.title,
            referenceDate: calendarApi.getDate(),
        }));
        getEvents(calendarApi.view.activeStart, calendarApi.view.activeEnd);
    };

    useImperativeHandle(ref, () => {
        return {
            moveToDate(date: moment.Moment) {
                handleDateChange(date);
            },
            moveToPrev() {
                handlePrev();
            },
            moveToNext() {
                handleNext();
            },
        };
    }, []);

    const [state, setState] = useState<CalendarState>({
        title: "",
        viewType: CalendarViewType.Month,
        detailsVisible: false,
    });
    
    const isDblClick = useRef(false);

    useEffect(() => {
        setState((old) => ({
            ...old,
            title: calendarRef?.current?.getApi().view.title,
        }));
    }, [calendarRef?.current?.getApi().view.title]);

    const handleToday = () => {
        const calendarApi = calendarRef?.current?.getApi();
        calendarApi.today();
        setState((old) => ({
            ...old,
            title: calendarApi.view?.title,
            referenceDate: calendarApi.getDate(),
        }));
        getEvents(calendarApi.view.activeStart, calendarApi.view.activeEnd);
    };

    const handleChangeView = (viewType: CalendarViewType) => {
        if (!viewType) return; //viewType may be null!
        const calendarApi = calendarRef?.current?.getApi();
        const date = calendarApi.getDate();
        calendarApi.changeView(viewType);
        setState((old) => ({
            ...old,
            title: calendarApi.view?.title,
            viewType
        }));
        getEvents(calendarApi.view.activeStart, calendarApi.view.activeEnd);
        calendarApi.gotoDate(date);
        calendarApi.select(date);
    };

    const handleEventClick = (event: EventClickArg) => {
        setState((old) => ({
            ...old,
            detailsVisible: true,
            detailsEvent: props.events?.find((item) => item.id === event.event.id),
            detailsAnchor: event.el,
        }));
    };

    const handleEventDoubleClick = (event: EventMountArg) => {
        props.onEventClick(event.event.id);
    };

    const handleDateClick = (arg: DateClickArg) => {
        if (isDblClick.current) {
            isDblClick.current = false;
            props.onDateClick(arg.date.getTime());
        } else {
            isDblClick.current = true;
            setTimeout(() => { isDblClick.current = false; }, 200);
            const calendarApi = calendarRef?.current?.getApi();
            calendarApi.gotoDate(arg.date);
            calendarApi.select(arg.date);
            setState((old) => ({
                ...old,
                title: calendarApi.view.title,
            }));
        }
    };

    const handleEventDrop = (event: EventDropArg) => {
        if (!event.event?.start) return;
        props.onSetEventStartDate(event.event.id, event.event.start.getTime());
    };

    const renderEventContent = (eventContent: EventContentArg) => (
        <>
            {!!eventContent.event.extendedProps && !!eventContent.event.extendedProps.private &&
                <Box sx={{ color: "#333" }}>
                    <ListStateIconPrivate />
                </Box>
            }
            <div className="fc-daygrid-event-dot" style={{ borderColor: eventContent.backgroundColor }}></div>
            <div className="fc-event-time">{eventContent.timeText}</div>
            <div className="fc-event-title">{eventContent.event.title}</div>
        </>
    );

    // This is called too often...
    // const handleEvents = (arg: EventSourceFuncArg, successCallback: (eventInputs: EventInput[]) => void, failureCallback: (error: Error) => void) => {
    //     if (isCalling.current) return;
    //     isCalling.current = true;
    //     //console.log("handleEvents 1", arg.start, arg.end, arg.timeZone);
    //     props.onGetEvents(arg.start.getTime(), arg.end.getTime())
    //         .then(events => {
    //             //console.log("handleEvents 2", events);
    //             successCallback(events);
    //         })
    //         .finally(() => {
    //             isCalling.current = false;
    //         });
    // };
    
    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "1rem",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <AppButton
                        variant="contained"
                        onClick={handleToday}
                        sx={{ marginRight: "1rem" }}
                    >
                        {Translations.Today}
                    </AppButton>
                    <AppToolButton onClick={handlePrev} title={Translations.Previous} icon={<ArrowBackIosNewIcon />} />
                    <CalendarDatePicker
                        referenceDate={state.referenceDate}
                        title={state.title}
                        onChange={handleDateChange}
                    />
                    <AppToolButton onClick={handleNext} title={Translations.Next} icon={<ArrowForwardIosIcon />} />
                </Box>
                <Box>
                    <ToggleButtonGroup
                        color="primary"
                        value={state.viewType}
                        exclusive
                        onChange={(e, value) => handleChangeView(value)}
                    >
                        <ToggleButton value={CalendarViewType.Day} sx={{ borderBottomLeftRadius: "8px", borderTopLeftRadius: "8px" }}>{Base.upperCaseFirst(Translations.Day)}</ToggleButton>
                        <ToggleButton value={CalendarViewType.Week}>{Translations.Week}</ToggleButton>
                        <ToggleButton value={CalendarViewType.Month} sx={{ borderBottomRightRadius: "8px", borderTopRightRadius: "8px" }}>{Translations.Month}</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>
            <FullCalendar
                ref={calendarRef}
                locales={[fiLocale]}
                locale={appConfig.culture.replace("-US", "")}
                plugins={[interactionPlugin, momentPlugin, dayGridPlugin]}
                initialView={CalendarViewType.Month}
                height={height}
                weekNumbers
                firstDay={1}
                headerToolbar={false}
                selectable
                eventContent={renderEventContent}
                eventClick={handleEventClick}
                dateClick={handleDateClick}
                events={props.events}
                eventDrop={handleEventDrop}
                eventDidMount={(event) => {
                    event.el.addEventListener("dblclick", () => {
                        handleEventDoubleClick(event);
                    });
                }}
            />
            {state.detailsVisible &&
                <CalendarItemDetailPopup
                    anchorEl={state.detailsAnchor}
                    event={state.detailsEvent}
                    onOpenDialog={props.onEventClick}
                    onRemoveItem={props.onEventRemove}
                    onClose={() => setState((old) => ({ ...old, detailsVisible: false }))}
                />
            }
        </>
    );
});
Calendar.displayName = "Calendar";